import { ImageFrame } from '@airship-platform/common/validation';
import { FileButton, FlexBox } from '@airship-platform/jam-web/components';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import Cropper, { Point } from 'react-easy-crop';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useToast } from '../../hooks';

const WELL_HEIGHT = 240;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const ImageFrameFieldSchema = z.object({
  framedImage: z
    .union([
      z.string(),
      z.object({
        file: z.instanceof(File),
        area: z
          .object({
            x: z.number(),
            y: z.number(),
            width: z.number().positive(),
            height: z.number().positive(),
          })
          .optional(),
      }),
    ])
    .optional()
    .refine(
      (framedImage) =>
        typeof framedImage !== 'object' ||
        ['image/jpeg', 'image/png', 'image/gif'].includes(
          framedImage.file.type
        ),
      { error: 'Please upload a JPEG, PNG, or GIF file.' }
    )
    .refine(
      (framedImage) =>
        typeof framedImage !== 'object' ||
        framedImage.file.type !== 'image/gif' ||
        framedImage.file.size <= MAX_FILE_SIZE,
      { error: 'File size must be smaller than 10 MB.' }
    ),
});

export type ImageFrameFieldValues = z.infer<typeof ImageFrameFieldSchema>;

interface ImageFrameFieldProps {
  frame: ImageFrame;
  isLoading: boolean;
}

const ImageFrameField: React.FC<ImageFrameFieldProps> = ({
  frame,
  isLoading,
}) => {
  const { control, watch } = useFormContext<ImageFrameFieldValues>();
  const [imageUrl, setImageUrl] = useState<string>();
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const { showToast } = useToast();

  const framedImage = watch('framedImage');
  const pickedFile =
    typeof framedImage === 'object' ? framedImage.file : undefined;

  useEffect(() => {
    if (pickedFile) {
      const url = URL.createObjectURL(pickedFile);
      setImageUrl(url);

      return () => {
        URL.revokeObjectURL(url);
        setImageUrl(undefined);
      };
    }
  }, [pickedFile]);

  const pickFile = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (framedImage: ImageFrameFieldValues['framedImage']) => void
  ) => {
    const file = event.target.files?.[0];

    if (file) {
      setCrop({ x: 0, y: 0 });
      setZoom(MIN_ZOOM);
      onChange({ file });
    }
  };

  const dropUnreadableImage = (
    onChange: (framedImage: ImageFrameFieldValues['framedImage']) => void
  ) => {
    showToast('We could not read this image. Try another file.', 'warning');
    onChange(undefined);
  };

  return (
    <Controller
      name={'framedImage'}
      control={control}
      render={({ field, fieldState }) => (
        <FlexBox
          flexDirection={'column'}
          gap={1}
          width={WELL_HEIGHT * frame.aspectRatio}
          maxWidth={'100%'}
        >
          <FlexBox
            position={'relative'}
            width={'100%'}
            aspectRatio={frame.aspectRatio}
            overflow={'hidden'}
            borderRadius={1}
            bgcolor={(theme) => theme.palette.gray?.[100]}
          >
            {pickedFile && imageUrl && (
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                minZoom={MIN_ZOOM}
                maxZoom={MAX_ZOOM}
                aspect={frame.aspectRatio}
                objectFit={'cover'}
                showGrid={false}
                zoomWithScroll={false}
                mediaProps={{
                  onError: () => dropUnreadableImage(field.onChange),
                }}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, area) =>
                  field.onChange({ file: pickedFile, area })
                }
              />
            )}
            {typeof framedImage === 'string' && (
              <Box
                component={'img'}
                src={framedImage}
                alt={'Current framing'}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </FlexBox>
          {pickedFile && (
            <Slider
              aria-label={'Zoom'}
              value={zoom}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.01}
              onChange={(_, value) => setZoom(value)}
            />
          )}
          {fieldState.error?.message && (
            <Typography variant={'body3'} color={'error'}>
              {fieldState.error.message}
            </Typography>
          )}
          <FlexBox gap={1}>
            <FileButton
              color={'blue'}
              variant={'text'}
              acceptedFiles={'image/jpeg,image/png,image/gif,image/svg+xml'}
              onChange={(event) => pickFile(event, field.onChange)}
              isLoading={isLoading}
            >
              {framedImage ? 'Replace' : 'Add Image'}
            </FileButton>
            {framedImage && (
              <Button
                color={'secondary'}
                variant={'text'}
                onClick={() => field.onChange(undefined)}
                disabled={isLoading}
              >
                Remove
              </Button>
            )}
          </FlexBox>
        </FlexBox>
      )}
    />
  );
};

export default ImageFrameField;
